import './Grid.css';

import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import { Column } from 'primereact/column';
import { Tooltip } from 'primereact/tooltip';
import {
  DataTable,
  DataTableDataSelectableEvent,
  DataTableRowClickEvent,
  DataTableStateEvent,
  DataTableValue,
} from 'primereact/datatable';
import { Skeleton } from 'primereact/skeleton';
import { formatValue } from '../../../utils/managerUtils';
import SeptimLoadingSpinner from '../SeptimLoadingSpinner/SeptimLoadingSpinner';
import CellContent from './components/CellContent/CellContent';
import { isNegativeHighlightedValue } from './utils/isNegativeHighlightedValue';
import { isNonZeroHighlightedValue } from './utils/isNonZeroHighlightedValue';
import { ColumnsWitdhType, FilterError, MatchMode } from '../../../types/types';
import { APP_HEADER_BREAKPOINT_PX, GRID_ROW_HEIGHT } from '../../../constants';
import EmptyMessageWithLoading from './components/EmptyMessageWithLoading/EmptyMessageWithLoading';
import FilterApplyTemplate from './components/FilterTemplates/FilterApplyTemplate';
import FilterClearTemplate from './components/FilterTemplates/FilterClearTemplate';
import FilterTemplate from './components/FilterTemplates/FilterTemplate';
import { applyFilterChange } from './utils/applyFilterChange';
import { getColumns } from './utils/getColumns';
import { getVisibleColumnsWidth } from './utils/getVisibleColumnsWidth';
import { getGridHeightString } from './utils/getGridHeightString';
import { getRowClassName } from './utils/getRowClassName';
import { getSkeletonRows } from './utils/getSkeletonRows';
import { isSkeletonRow } from './utils/isSkeletonRow';
import { SelectedRowsContextType } from '../../../types/context';
import { useGridScrollBehavior } from './hooks/useGridScrollBehavior';
import { useColumnFilterOverlayClip } from './hooks/useColumnFilterOverlayClip';
import { useCellTooltip } from './hooks/useCellTooltip';
import { useExpandedRowTracking } from './hooks/useExpandedRowTracking';
import ExpandedRowAnimation from './components/ExpandedRowAnimation/ExpandedRowAnimation';
import ColumnFilterRegistryContextProvider from './context/ColumnFilterRegistryContext/ColumnFilterRegistryContext';
import { COLUMN_NAME_ATTR, QUICK_FILTER_COLUMN_ATTR } from '../../../constants';
import { useColumnTriggerRegistrations } from '../../../hooks/shortcuts/useColumnTriggerRegistrations';
import { useQuickColumnFilterShortcut } from '../../../hooks/shortcuts/useQuickColumnFilterShortcut';
import { useIsViewportNarrowerThan } from '../../../hooks/useIsViewportNarrowerThan';
import { GridColumn } from './types';
import { handleTbodyMouseDown } from './utils/handleTbodyMouseDown';
import { useInsertDatasetData } from '../../../api/hooks/useInsertDatasetData';
import { DatasetItem } from '../../../types/types';
import InlineNumberCell from './components/InlineNumberCell/InlineNumberCell';
import { isDatasetConditionMet } from './utils/isDatasetConditionMet';

const SCROLL_CONTAINER_SELECTOR = '.p-datatable-wrapper';
const VIEW_COLUMNS_INSERT_API = '/webManager/1.0/forms/viewColumns/insert';

type ColumnReorderEvent = {
  columns: Array<{ props?: { field?: unknown } }>;
};

interface GridProps {
  dataset: any;
  filter: any;
  data: any;
  rowExpansionTemplate?: any;
  disableRowExpansion?: boolean;
  dataLoading: any;
  selectedRows: any;
  lazyState: any;
  setLazyState: Dispatch<SetStateAction<DataTableStateEvent>>;
  expandedRow?: any;
  setExpandedRow?: Dispatch<SetStateAction<any>>;
  onHorizontalScroll?: (value: number) => void;
  loadMoreRows: () => void;
  dataKey: string;
  gridRef: RefObject<any>;
  disabledLazyLoading?: boolean;
  columnsWidth: ColumnsWitdhType;
  gridHeaderHeight?: number;
  enabledVirtualScroller?: boolean;
  emptyMessageRootSelector?: string;
  referenceDatasets: any;
  setReferenceDatasets: Dispatch<any>;
  isSlaveGrid?: boolean;
  disableMultiselect?: boolean;
  openFullscreenReference?: (
    column: GridColumn,
    value: string | null,
    matchMode: MatchMode,
    label: string,
    resolvedFilter?: Record<string, any> | null
  ) => void;
  onRowToggle?: (row: any) => void;
  onRowDoubleClick?: (e: DataTableRowClickEvent) => void;
  isReferenceGrid?: boolean;
  lockedFilterParams?: string[];
  selectAllChecked: SelectedRowsContextType['selectAllChecked'];
  processSelectAllChange: SelectedRowsContextType['processSelectAllChange'];
  processSelectedRowsChange: SelectedRowsContextType['processSelectedRowsChange'];
  resetSelectedRows: SelectedRowsContextType['resetSelectedRows'];
  onViewColumnsSaved?: () => Promise<void>;
  // Optional wider DOM scope for the quick-filter shortcut (e.g. slave grid expansion area); defaults to the gridRef container.
  shortcutScopeRef?: RefObject<HTMLElement | null>;
  // One-shot scroll target for the reload-action locate flow; see useGridScrollBehavior.
  scrollTargetOverrideRef?: SelectedRowsContextType['reloadLocateScrollTargetRef'];
  inlineEdit?: {
    masterRow?: Record<string, unknown> | null;
    onSave: (
      row: Record<string, unknown>,
      column: DatasetItem,
      value: number
    ) => Promise<boolean>;
  };
}

const GridContent = ({
  dataset,
  filter,
  data,
  rowExpansionTemplate = null,
  disableRowExpansion = false,
  dataLoading,
  selectedRows,
  lazyState,
  setLazyState,
  expandedRow,
  setExpandedRow,
  onHorizontalScroll,
  loadMoreRows,
  dataKey,
  gridRef,
  disabledLazyLoading = true,
  columnsWidth,
  gridHeaderHeight = 60,
  enabledVirtualScroller = true,
  emptyMessageRootSelector = SCROLL_CONTAINER_SELECTOR,
  referenceDatasets,
  setReferenceDatasets,
  isSlaveGrid = false,
  disableMultiselect = false,
  openFullscreenReference,
  onRowToggle,
  onRowDoubleClick,
  isReferenceGrid = false,
  lockedFilterParams,
  processSelectAllChange,
  processSelectedRowsChange,
  resetSelectedRows,
  onViewColumnsSaved,
  selectAllChecked,
  shortcutScopeRef,
  scrollTargetOverrideRef,
  inlineEdit,
}: GridProps) => {
  const [filterErrors, setFilterErrors] = useState<Record<string, FilterError>>(
    {}
  );
  const [closingRowData, setClosingRowData] = useState<any>(null);
  const justOpenedRowRef = useRef<any>(null);
  const pendingColumnOrderRef = useRef<string[] | null>(null);
  const isColumnOrderSaveRunningRef = useRef(false);
  const { insert: insertViewColumns } = useInsertDatasetData();
  const {
    expandedIndex,
    expandedRowHeight,
    setExpandedRowHeight,
    heldExpandedHeightRef,
    handleExpandedHeightChange,
  } = useExpandedRowTracking({ data, expandedRow, dataKey });

  const { cellTooltipId, cellTooltipRef, rebindCellTooltip } = useCellTooltip();

  // Overflow detection must sum only rendered columns; columnsWidth still carries
  // hidden columns (measured from the raw dataset), which would force a false overflow.
  const visibleColumnsWidth = useMemo(
    () => getVisibleColumnsWidth(columnsWidth, dataset.items),
    [columnsWidth, dataset.items]
  );

  const { hasHorizontalOverflow, hasVerticalOverflow, markUserToggle } =
    useGridScrollBehavior({
      gridRef,
      enabledVirtualScroller,
      columnsWidth: visibleColumnsWidth,
      expandedRow,
      selectedRows,
      data,
      dataKey,
      loadMoreRows,
      disabledLazyLoading,
      onHorizontalScroll,
      scrollTargetOverrideRef,
    });

  // Fixes column filter popover overlapping sidebar when scrolling horizontally with open filter
  useColumnFilterOverlayClip(gridRef);

  const hasDataToDisplay: boolean = data && data.length > 0;
  const columns: GridColumn[] = useMemo(
    () =>
      getColumns(
        dataset,
        filter,
        lazyState,
        columnsWidth,
        hasHorizontalOverflow,
        lockedFilterParams
      ),
    [
      dataset,
      columnsWidth,
      hasHorizontalOverflow,
      filter,
      lazyState,
      lockedFilterParams,
    ]
  );

  useColumnTriggerRegistrations(columns, gridRef);
  useQuickColumnFilterShortcut({
    columns,
    multiSortMeta: lazyState?.multiSortMeta,
    gridRef,
    scopeRef: shortcutScopeRef ?? gridRef,
    disabled: Boolean(dataLoading),
    shortcutId: dataset.datasetId ?? dataset.name ?? 'grid',
    // Slave subset grid swallows the shortcut even when it has no filterable column so the trigger does not bubble up to the master grid.
    claimWhenEmpty: isSlaveGrid,
  });

  useEffect(() => {
    if (dataset) {
      const newFilterErrors = {};
      dataset.items.forEach((column) => {
        if (column.visible !== false && column.filterParam) {
          newFilterErrors[column.name] = null;
        }
      });
      setFilterErrors(newFilterErrors);
    }
  }, [dataset]);

  const handleCloseAnimationEnd = useCallback(() => {
    setExpandedRow?.(null);
    setClosingRowData(null);
    setExpandedRowHeight(0);
    onRowToggle?.(null);
  }, [setExpandedRow, onRowToggle]);

  const toggleExpandedRow = (row) => {
    if (
      !isSkeletonRow(row[dataKey]) &&
      setExpandedRow &&
      (selectedRows.length === 0 || isReferenceGrid)
    ) {
      // Compare by dataKey, not reference: a reload/refetch swaps in fresh row
      // objects, so the stored expandedRow ref no longer matches the rendered
      // row. Reference equality would misread a close-click as a re-open,
      // forcing a second click to actually close the row.
      const isOpening = expandedRow?.[dataKey] !== row[dataKey];

      if (isOpening) {
        justOpenedRowRef.current = row;
        setClosingRowData(null);
        // Drop any held height synchronously so a different row's expansion mounts
        // fresh (initialHeight 0), with no stale hold from the previous row.
        heldExpandedHeightRef.current = 0;
        if (isSlaveGrid || disableRowExpansion) markUserToggle();
        setExpandedRow(row);
        onRowToggle?.(row);
        return;
      }

      // Nothing mounted to animate the close, so tear down now.
      if (disableRowExpansion) {
        handleCloseAnimationEnd();
        return;
      }

      // Defer actual removal until close animation finishes
      setClosingRowData(row);
    }
  };

  const expansionEnabled: boolean =
    Boolean(rowExpansionTemplate) && !disableRowExpansion;

  const wrappedRowExpansionTemplate = useCallback(
    (row: any) => (
      <ExpandedRowAnimation
        isClosing={closingRowData === row}
        onCloseEnd={handleCloseAnimationEnd}
        skipOpenAnimation={justOpenedRowRef.current !== row}
        onOpenAnimationStart={() => {
          justOpenedRowRef.current = null;
        }}
        onHeightChange={handleExpandedHeightChange}
        initialHeight={heldExpandedHeightRef.current}
      >
        {rowExpansionTemplate?.(row)}
      </ExpandedRowAnimation>
    ),
    [
      rowExpansionTemplate,
      closingRowData,
      handleCloseAnimationEnd,
      handleExpandedHeightChange,
    ]
  );

  const isNarrowAppHeader = useIsViewportNarrowerThan(APP_HEADER_BREAKPOINT_PX);
  const gridHeightString = getGridHeightString(
    isSlaveGrid,
    gridHeaderHeight,
    isNarrowAppHeader
  );

  const onFilter = useCallback(
    (e: DataTableStateEvent) => {
      applyFilterChange(
        lazyState.filters,
        e,
        dataset,
        setLazyState,
        resetSelectedRows
      );
    },
    // setLazyState stays out: SlaveGrid passes an inline arrow, so including it
    // would rebuild onFilter on every render.
    [lazyState, dataset, resetSelectedRows]
  );

  const getBodyContent = useCallback(
    (rowdata: any, column: GridColumn) => {
      if (isSkeletonRow(rowdata[dataKey])) {
        return <Skeleton width="70%" />;
      }
      const value = formatValue(rowdata, column);
      if (
        column.inlineEdit &&
        inlineEdit &&
        isDatasetConditionMet(
          column.inlineEdit.enabledWhen,
          rowdata,
          inlineEdit.masterRow
        )
      ) {
        return (
          <InlineNumberCell
            columnName={column.name}
            value={rowdata[column.name]}
            formattedValue={value}
            min={column.inlineEdit.min}
            max={column.inlineEdit.max}
            onSave={(newValue) => inlineEdit.onSave(rowdata, column, newValue)}
          />
        );
      }
      const highlightNegative = isNegativeHighlightedValue(
        rowdata[column.name],
        column.highlightNegative === true
      );
      const highlightNonZero = isNonZeroHighlightedValue(
        rowdata[column.name],
        column.highlightNonZero === true
      );
      return (
        <CellContent
          value={value}
          highlightDanger={highlightNegative || highlightNonZero}
        />
      );
    },
    [dataKey, inlineEdit]
  );

  const skeletonRows = useMemo(() => getSkeletonRows(dataKey), [dataKey]);

  const tableValue = useMemo(
    () =>
      data && !disabledLazyLoading && enabledVirtualScroller
        ? [...data, ...skeletonRows]
        : data,
    [data, skeletonRows, disabledLazyLoading, enabledVirtualScroller]
  );

  const onRowClick = useCallback(
    (e: DataTableRowClickEvent) => toggleExpandedRow(e.data),
    [expandedRow, selectedRows]
  );
  const isDataSelectable = useCallback(
    (e: DataTableDataSelectableEvent) => !isSkeletonRow(e.data[dataKey]),
    [dataKey]
  );
  const rowClassName = useCallback(
    (rowData: DataTableValue) =>
      getRowClassName(rowData, dataKey, expandedRow, dataset),
    [expandedRow, dataKey, dataset]
  );

  const hasActiveFilters: boolean = useMemo(
    () => columns.some((column) => column.filterActive),
    [columns]
  );

  const persistPendingColumnOrder = useCallback(async () => {
    if (isColumnOrderSaveRunningRef.current) return;
    isColumnOrderSaveRunningRef.current = true;

    try {
      let hasSavedSinceReload = false;
      while (pendingColumnOrderRef.current || hasSavedSinceReload) {
        while (pendingColumnOrderRef.current) {
          const selected = pendingColumnOrderRef.current;
          pendingColumnOrderRef.current = null;
          const result = await insertViewColumns(VIEW_COLUMNS_INSERT_API, {
            datasetId: String(dataset.datasetId),
            selected: selected.join(','),
          });
          if (!result?.error) hasSavedSinceReload = true;
        }
        if (!hasSavedSinceReload) continue;
        hasSavedSinceReload = false;
        await onViewColumnsSaved?.();
      }
    } finally {
      isColumnOrderSaveRunningRef.current = false;
    }
  }, [dataset.datasetId, insertViewColumns, onViewColumnsSaved]);

  const onColReorder = useCallback(
    (event: ColumnReorderEvent) => {
      const canPersist =
        !isSlaveGrid &&
        !isReferenceGrid &&
        dataset.datasetControls?.some(
          (control: { name?: string }) => control.name === 'viewColumns'
        );
      if (!canPersist) return;

      const visibleNames = new Set(columns.map((column) => column.name));
      const seen = new Set<string>();
      const orderedNames = event.columns.flatMap((column) => {
        const field = column.props?.field;
        if (
          typeof field !== 'string' ||
          !visibleNames.has(field) ||
          seen.has(field)
        ) {
          return [];
        }
        seen.add(field);
        return [field];
      });

      // Never replace a complete saved view with a partial PrimeReact event.
      if (orderedNames.length !== visibleNames.size) return;
      pendingColumnOrderRef.current = orderedNames;
      void persistPendingColumnOrder();
    },
    [columns, dataset.datasetControls, isReferenceGrid, isSlaveGrid, persistPendingColumnOrder]
  );

  // `data` is an array only after a fetch resolved (null/undefined before).
  // Keep the empty message hidden until then so it does not flash centered
  // for the frame before `dataLoading` flips true.
  const isDataLoaded: boolean = Array.isArray(data);

  // Widths are measured on the first resolved fetch and cleared only on a dataset change.
  const hasMeasuredColumnWidths: boolean = columnsWidth !== undefined;

  const containerClassName = clsx(
    // Hide the header only on a dataset's first load, so a filter or sort refetch keeps the column names.
    (isDataLoaded || hasMeasuredColumnWidths || hasActiveFilters) &&
      'grid-header-visible',
    hasVerticalOverflow && !expansionEnabled
      ? 'grid-container grid-container-scrolling'
      : 'grid-container',
    !hasDataToDisplay && 'grid-empty',
    cellTooltipId
  );

  return (
    <div className={containerClassName} ref={gridRef}>
      <DataTable
        // Remount on toggle: primereact never rebinds its resize listener otherwise.
        key={enabledVirtualScroller ? 'vs' : 'plain'}
        emptyMessage={
          <EmptyMessageWithLoading
            dataLoading={dataLoading}
            isDataLoaded={isDataLoaded}
            targetSelector={emptyMessageRootSelector}
            containerRef={gridRef}
          />
        }
        cellMemoProps={['rowData']}
        className={dataLoading ? 'grid grid-loading' : 'grid'}
        isDataSelectable={isDataSelectable}
        style={dataLoading ? { minHeight: gridHeightString } : {}}
        value={tableValue}
        scrollable
        scrollHeight={gridHeightString}
        {...(enabledVirtualScroller && {
          virtualScrollerOptions: {
            lazy: true,
            itemSize: GRID_ROW_HEIGHT,
            onScroll: rebindCellTooltip,
            expandedIndex,
            expandedSize: expandedIndex >= 0 ? expandedRowHeight : 0,
            // Default 10ms is shorter than a frame, so a resize drag reinits every frame.
            resizeDelay: 150,
          },
        })}
        selectionMode="checkbox"
        selection={selectedRows}
        onSelectionChange={processSelectedRowsChange}
        onSelectAllChange={processSelectAllChange}
        selectAll={selectAllChecked}
        onRowClick={onRowClick}
        {...(onRowDoubleClick && { onRowDoubleClick })}
        // Prevents text selection flashing on double click
        {...(onRowDoubleClick && {
          pt: { tbody: { onMouseDown: handleTbodyMouseDown } },
        })}
        {...(rowExpansionTemplate &&
          !disableRowExpansion && {
            expandedRows: expandedRow ? [expandedRow] : [],
            rowExpansionTemplate: wrappedRowExpansionTemplate,
          })}
        resizableColumns
        columnResizeMode="expand"
        reorderableColumns
        onColReorder={onColReorder}
        tableStyle={{
          tableLayout: columnsWidth ? 'fixed' : 'auto',
        }}
        onSort={setLazyState}
        multiSortMeta={lazyState.multiSortMeta}
        sortMode="multiple"
        loading={dataLoading}
        onFilter={onFilter}
        filters={lazyState.filters}
        rowClassName={rowClassName}
        loadingIcon={<SeptimLoadingSpinner />}
        dataKey={dataKey}
        lazy
      >
        {dataset.rowSettings?.enableCheckboxColumn !== false &&
          !disableMultiselect && (
            <Column
              field="select"
              reorderable={false}
              resizeable={false}
              selectionMode="multiple"
              style={{ width: '48px', maxWidth: '48px' }}
            />
          )}
        {columns.map((column) => (
          <Column
            pt={{
              filterMenuButton: () => ({
                [QUICK_FILTER_COLUMN_ATTR]: column.name,
              }),
              headerCell: () => ({ [COLUMN_NAME_ATTR]: column.name }),
            }}
            headerClassName={column.filterActive ? 'filter-active' : ''}
            dataType={column.dataType}
            headerTooltip={column.hint}
            field={column.name}
            columnKey={column.name}
            header={column.label}
            showFilterOperator={false}
            showFilterMenuOptions={!!column.filterItem}
            showAddButton={false}
            filterApply={(options) => (
              <FilterApplyTemplate
                options={options}
                column={column}
                filterErrors={filterErrors}
                setFilterErrors={setFilterErrors}
              />
            )}
            filterClear={(options) => (
              <FilterClearTemplate
                options={options}
                column={column}
                setFilterErrors={setFilterErrors}
              />
            )}
            headerTooltipOptions={{
              position: 'bottom',
              mouseTrack: true,
              className: 'grid-tooltip',
              style: {
                maxWidth: column.style?.maxWidth
                  ? `${column.style.maxWidth}em`
                  : undefined,
              },
            }}
            body={(rowdata) => getBodyContent(rowdata, column)}
            style={column.style}
            bodyClassName={column.bodyClassName}
            sortable
            filter={column.hasFilter}
            filterMatchModeOptions={column.matchModes}
            filterElement={(options) => (
              <FilterTemplate
                options={options}
                column={column}
                filterErrors={filterErrors}
                setFilterErrors={setFilterErrors}
                referenceDatasets={referenceDatasets}
                setReferenceDatasets={setReferenceDatasets}
                openFullscreenReference={
                  openFullscreenReference
                    ? (filterColumn, value, matchMode, resolvedFilter) =>
                        openFullscreenReference(
                          filterColumn,
                          value,
                          matchMode,
                          `${dataset.label}: ${column.label}`,
                          resolvedFilter
                        )
                    : undefined
                }
              />
            )}
          />
        ))}
      </DataTable>
      <Tooltip
        ref={cellTooltipRef}
        target={`.${cellTooltipId} .cell-content`}
        mouseTrack
        position="bottom"
        className="grid-tooltip cell-tooltip"
      />
    </div>
  );
};

const Grid = (props: GridProps) => (
  <ColumnFilterRegistryContextProvider>
    <GridContent {...props} />
  </ColumnFilterRegistryContextProvider>
);

export default Grid;
